import { AttachmentDisplay } from '@/components/shared/AttachmentDisplay';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import {
  FileUploadStatus,
  GetAssemblyDetailsDocument,
  useUpdateFileStatusMutation,
  useAssemblyImageCaptionMutation,
} from '@/__generated__/graphql';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useEffect, useMemo, useState } from 'react';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { uploadFileToUrl } from '../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/Tabs/UploadFileToUrl';

type Props = {
  referenceId: string;
  collectionName: string;
  selectedAttachment: Attachment;
  selectedAttachmentIndex: number;
  handleRemoveAttachment?: (index: number) => void;
  showRemoveIcon: boolean;
  enableUpload?: boolean;
  onUploadComplete?: (index: number, file: Attachment) => void;
  isUploadDialog?: boolean;
};

export const FileDisplay = ({
  referenceId,
  collectionName,
  selectedAttachment,
  selectedAttachmentIndex,
  handleRemoveAttachment,
  showRemoveIcon,
  enableUpload = false,
  onUploadComplete,
  isUploadDialog,
}: Props) => {
  const screenSize = useWindowSize();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [imageCaption, setImageCaption] = useState<string>(selectedAttachment.name);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [isEditing, setIsEditing] = useState(false);
  const [UpdateFileStatus] = useUpdateFileStatusMutation();
  const [assemblyImageCaption] = useAssemblyImageCaptionMutation();

  useEffect(() => {
    const uploadAttachments = async () => {
      if (
        enableUpload &&
        selectedAttachment.isNew &&
        selectedAttachment?.url &&
        selectedAttachment.file &&
        selectedAttachment?.id
      ) {
        const uploadResult = await uploadFileToUrl(
          selectedAttachment,
          selectedAttachment?.url,
          setUploadProgress
        );
        if (uploadResult) {
          await UpdateFileStatus({
            variables: {
              input: {
                referenceId,
                collectionName,
                fileUpdates: [
                  { uploadStatus: FileUploadStatus.Success, fileId: selectedAttachment.id },
                ],
              },
            },
            onError: (error) => {
              showSnackBar(error.message, 'error');
            },
          });
          if (onUploadComplete) onUploadComplete(selectedAttachmentIndex, selectedAttachment);
        }
      }
    };

    if (enableUpload) {
      uploadAttachments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableUpload,
    collectionName,
    selectedAttachment,
    selectedAttachmentIndex,
    referenceId,
    UpdateFileStatus,
    onUploadComplete,
  ]);

  const handleImageCaption = async () => {
    setIsEditing(false);
    if (
      referenceId &&
      selectedAttachment?.id &&
      imageCaption.trim() &&
      imageCaption !== selectedAttachment.caption
    ) {
      try {
        await assemblyImageCaption({
          variables: {
            input: {
              assemblyId: referenceId,
              assemblyAttachmentId: selectedAttachment.id,
              caption: imageCaption,
              lastModifiedFor: 'Update attachment caption',
            },
          },
          refetchQueries: [GetAssemblyDetailsDocument],
        });
        showSnackBar('Image caption added', 'success');
      } catch (error) {
        showSnackBar('Failed to add image caption', 'error');
      }
    }
  };

  useEffect(() => {
    setImageCaption(selectedAttachment.caption ?? selectedAttachment.name);
  }, [selectedAttachment]);

  const handleCaption = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const cardHeight = useMemo(() => {
    const { width } = screenSize;
    return width && width <= 1440 ? '60px' : '80px';
  }, [screenSize]);

  return (
    <AttachmentDisplay
      selectedAttachment={selectedAttachment}
      selectedAttachmentIndex={selectedAttachmentIndex}
      showRemoveIcon={showRemoveIcon}
      handleRemoveAttachment={handleRemoveAttachment}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      imageCaption={imageCaption}
      setImageCaption={setImageCaption}
      handleImageCaption={handleImageCaption}
      handleCaption={handleCaption}
      uploadProgress={uploadProgress}
      enableUpload={enableUpload}
      cardHeight={cardHeight}
      isUploadDialog={isUploadDialog}
    />
  );
};
